'use client';

/**
 * Live insets for the "classroom" reference screen (canvas 1586×992).
 *
 * Covers the painted cause → action card on the right with a live panel for
 * the selected synthetic learner, and moves a lime selection ring over the
 * selected learner node. The painted counters, the connector line from
 * Kirill and everything else stay as the artist painted them.
 */

import { useRef, type CSSProperties } from 'react';
import { rectStyle, SCENE_CANVAS, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './classroom.module.css';

const C = SCENE_CANVAS.classroom;

/** Measured on the PNG: card border 1311..1555 × 593..877. */
const CARD: Rect = [1311, 593, 245, 285];
/** Lime "Применить решение" fill, measured 1327..1539 × 823..861. */
const ACTION_BUTTON_ID = 'class-action';

/** Learner node centres from reference/hotspots.ts; every node rect is 92×82. */
const LEARNER_W = 92;
const LEARNER_H = 82;
const LEARNERS = [
  ['Анна', 'Anna', 971, 207],
  ['Богдан', 'Bogdan', 1148, 228],
  ['Виктория', 'Victoria', 1283, 308],
  ['Даниил', 'Daniil', 1330, 415],
  ['Ева', 'Eva', 1284, 519],
  ['Захар', 'Zakhar', 1185, 626],
  ['Ирина', 'Irina', 1060, 671],
  ['Кирилл', 'Kirill', 867, 665],
  ['Лера', 'Lera', 730, 604],
  ['Максим', 'Maxim', 640, 468],
  ['Назар', 'Nazar', 692, 315],
  ['Ольга', 'Olga', 797, 236],
] as const;

interface LearnerCopy {
  cause: { ru: string; en: string };
  action: { ru: string; en: string };
}

/**
 * Synthetic class 9Б, physics. Learner 8 is the painted case; the others
 * follow the painted signal of each node (check, amber mark, dim, growth).
 */
const CARDS: readonly LearnerCopy[] = [
  { cause: { ru: 'Затруднений нет: тема идёт уверенно', en: 'No difficulty seen: the topic is going well' }, action: { ru: 'Задача на перенос: та же идея в новой ситуации', en: 'A transfer task: the same idea in a new setting' } },
  { cause: { ru: 'Путает массу и вес в задачах на силу тяжести', en: 'Mixes up mass and weight in gravity problems' }, action: { ru: 'Короткое объяснение различия и две задачи', en: 'A short note on the difference and two problems' } },
  { cause: { ru: 'Последние ответы верные, темп ровный', en: 'Recent answers correct, steady pace' }, action: { ru: 'Сохранить темп: следующий блок по импульсу', en: 'Keep the pace: next block on momentum' } },
  { cause: { ru: 'Нет ответов по третьему закону Ньютона', en: 'No answers yet on Newton’s third law' }, action: { ru: 'Мини-объяснение и один проверочный вопрос', en: 'A mini explanation and one check question' } },
  { cause: { ru: 'Ошибки в единицах при расчёте ускорения', en: 'Unit errors when computing acceleration' }, action: { ru: 'Разбор одной ошибки и тренировка на единицах', en: 'Walk through one error, then unit practice' } },
  { cause: { ru: 'Три задания открыты, ни одно не сдано', en: 'Three tasks opened, none submitted' }, action: { ru: 'Спросить, что мешает, и дать один короткий шаг', en: 'Ask what is in the way and give one short step' } },
  { cause: { ru: 'Устойчивый результат по всей теме', en: 'A stable result across the topic' }, action: { ru: 'Предложить объяснить решение соседу по парте', en: 'Invite her to explain a solution to a deskmate' } },
  { cause: { ru: 'Пробел в теме «Законы Ньютона»', en: 'A gap in the topic Newton’s laws' }, action: { ru: 'Рекомендовать упражнение и мини-объяснение', en: 'Recommend an exercise and a mini explanation' } },
  { cause: { ru: 'Решает верно, но медленно: мало уверенности', en: 'Solves correctly but slowly: confidence is low' }, action: { ru: 'Три достижимые задачи с быстрой обратной связью', en: 'Three achievable problems with quick feedback' } },
  { cause: { ru: 'Заметный рост после разбора ошибок', en: 'Clear growth after the error review' }, action: { ru: 'Задача сложнее: система тел на нити', en: 'A harder problem: bodies connected by a string' } },
  { cause: { ru: 'Не хватает предпосылки: сложение векторов', en: 'A missing prerequisite: adding vectors' }, action: { ru: 'Вернуть основу: короткий блок по векторам', en: 'Restore the base: a short block on vectors' } },
  { cause: { ru: 'Верные ответы без записи решения', en: 'Correct answers without a written solution' }, action: { ru: 'Попросить оформить одно решение полностью', en: 'Ask for one fully written solution' } },
];

const T = {
  cause: { ru: 'Причина', en: 'Cause' },
  action: { ru: 'Действие', en: 'Action' },
  apply: { ru: 'Применить решение', en: 'Apply solution' },
  applyLabel: { ru: 'Применить решение и перейти к причинной цепочке', en: 'Apply solution and continue to the causal chain' },
  ringLabel: { ru: 'Выбран ученик', en: 'Selected learner' },
} as const;

/** True once the keyed value differs from its first-render value: insets never animate on scene mount. */
function useChanged(value: unknown): boolean {
  const initial = useRef(value);
  const changed = useRef(false);
  if (!Object.is(value, initial.current)) changed.current = true;
  return changed.current;
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" />
      <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8.5 17.5a6.5 6.5 0 1 1 7 0v1.8a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M10 22h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export default function ClassroomInsets({ lang, state, navigate, spotlight, onInteraction }: InsetProps) {
  const index = Math.min(LEARNERS.length, Math.max(1, state.selectedStudent)) - 1;
  const learner = LEARNERS[index];
  const copy = CARDS[index];
  const live = useChanged(index);
  const pick = <V,>(pair: { ru: V; en: V }): V => (lang === 'ru' ? pair.ru : pair.en);
  const name = lang === 'ru' ? learner[0] : learner[1];

  const ringStyle: CSSProperties & Record<'--rx' | '--ry', string> = {
    width: `${(LEARNER_W / C.width) * 100}%`,
    height: `${(LEARNER_H / C.height) * 100}%`,
    // Translate in --sn-unit (1 % of canvas width) so only transform animates.
    '--rx': `${((learner[2] - LEARNER_W / 2) / C.width) * 100}`,
    '--ry': `${((learner[3] - LEARNER_H / 2) / C.width) * 100}`,
  };

  const applySpot = spotlight === ACTION_BUTTON_ID;
  const applyLabel = pick(T.applyLabel);

  return (
    <div className={styles.root}>
      <i
        className={styles.ring}
        style={ringStyle}
        data-live={live || undefined}
        role="img"
        aria-label={`${pick(T.ringLabel)}: ${index + 1}, ${name}`}
      />

      <section className={styles.card} style={rectStyle(CARD, C)} data-live={live || undefined} aria-label={`${pick(T.cause)} / ${pick(T.action)}: ${name}`}>
        <div key={index} className={styles.body}>
          <p className={styles.who} style={{ '--i': 0 } as CSSProperties}>
            <span className={styles.num}>{index + 1}</span>
            {name}
          </p>
          <h3 className={styles.label} style={{ '--i': 1 } as CSSProperties}>
            <TargetIcon />
            {pick(T.cause)}
          </h3>
          <p className={styles.text} style={{ '--i': 2 } as CSSProperties}>{pick(copy.cause)}</p>
          <span className={styles.arrow} style={{ '--i': 3 } as CSSProperties} aria-hidden="true">↓</span>
          <h3 className={styles.label} style={{ '--i': 4 } as CSSProperties}>
            <BulbIcon />
            {pick(T.action)}
          </h3>
          <p className={styles.text} style={{ '--i': 5 } as CSSProperties}>{pick(copy.action)}</p>
          <button
            type="button"
            className={styles.apply}
            data-hotspot={ACTION_BUTTON_ID}
            data-spotlight={applySpot ? 'true' : undefined}
            aria-label={applyLabel}
            onClick={() => {
              onInteraction?.(applyLabel);
              navigate('motivation');
            }}
          >
            <span>{pick(T.apply)}</span>
            <span aria-hidden="true">→</span>
            {applySpot && <i className={styles.pulse} aria-hidden="true" />}
          </button>
        </div>
        <i key={`relight-${index}`} className={styles.relight} aria-hidden="true" />
      </section>
    </div>
  );
}
